import { useLang } from "../i18n";
import { fmtMoney, fmtSigned, fmtPct, pnlColor, regimeColor } from "../lib/format";
import { Activity } from "lucide-react";

function Metric({ label, value, sub, valueClass = "", testid, accent }) {
  return (
    <div data-testid={testid}
      className="border border-[#1E2028] bg-[#0F1015] p-4 hover:border-[#4B5563] hover:-translate-y-[1px] transition-[transform,border-color] duration-200"
      style={accent ? { borderTop: `2px solid ${accent}` } : {}}>
      <div className="text-[10px] uppercase tracking-[0.15em] text-[#6B7280] font-mono">{label}</div>
      <div className={`mt-2 text-2xl font-mono tabular font-medium ${valueClass}`}>{value}</div>
      {sub != null && <div className="mt-1 text-xs text-[#9CA3AF] font-mono">{sub}</div>}
    </div>
  );
}

export default function MetricsGrid({ account, regime }) {
  const { t } = useLang();
  if (!account) return null;
  const best = regime?.best_opportunity;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <Metric testid="metric-balance" label={t("balance")} value={fmtMoney(account.balance)}
        sub={account.micro_capital ? t("microCapital") : `Equity ${fmtMoney(account.equity)}`} />
      <Metric testid="metric-equity" label={t("equity")} value={fmtMoney(account.equity)}
        sub={`${t("availMargin")}: ${fmtMoney(account.available_margin)}`} />
      <Metric testid="metric-today-pnl" label={t("todayPnl")} value={fmtSigned(account.today_pnl)}
        valueClass={pnlColor(account.today_pnl)} accent={account.today_pnl >= 0 ? "#10B981" : "#EF4444"}
        sub={fmtPct(account.today_pnl / Math.max(account.balance, 1) * 100)} />
      <Metric testid="metric-total-pnl" label={t("totalPnl")} value={fmtSigned(account.total_pnl)}
        valueClass={pnlColor(account.total_pnl)}
        sub={`${t("drawdown")}: ${account.drawdown_pct}%`} />

      <Metric testid="metric-open-positions" label={t("openPositions")} value={account.open_positions_count}
        sub={`Max ${account.limits?.max_positions}`} />
      <Metric testid="metric-risk-exposure" label={t("riskExposure")} value={`${account.risk_exposure_pct}%`}
        sub={`${t("riskProfile")}: ${account.risk_profile}`} />
      <Metric testid="metric-ai-confidence" label={t("aiConfidence")}
        value={`${regime?.avg_confidence ?? "—"}`} accent="#FACC15"
        sub={best ? `${best.symbol} ${best.confidence}/100` : "—"}
        valueClass="text-[#FACC15]" />
      <div data-testid="metric-regime"
        className="border border-[#1E2028] bg-[#0F1015] p-4" style={{ borderTop: `2px solid ${regimeColor(regime?.dominant_regime)}` }}>
        <div className="text-[10px] uppercase tracking-[0.15em] text-[#6B7280] font-mono flex items-center gap-1.5">
          <Activity className="w-3 h-3" /> {t("marketRegime")}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <span className="live-dot inline-block w-2 h-2 rounded-full" style={{ background: regimeColor(regime?.dominant_regime) }} />
          <span className="text-sm font-display font-bold" style={{ color: regimeColor(regime?.dominant_regime) }}>
            {regime?.dominant_regime || "—"}
          </span>
        </div>
        <div className="mt-1 text-xs text-[#9CA3AF] font-mono truncate">
          {best ? `${t("bestOpportunity")}: ${best.symbol} ${best.direction}` : "—"}
        </div>
      </div>
    </div>
  );
}
